import {
  Component,
  HostListener,
  inject,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { Connection } from '../../models/connection.model';
import { CdfiForm } from '../../models/cdfi-form.model';
import { MicrosoftClarityService } from '../../../../shared/services/microsoft-clarity/microsoft-clarity.service';
import { CDFI_INFO } from '../../../../shared/utils/cdfi-info';
import { OutreachService } from '../../services/outreach.service';
import { OutreachFormData } from '../../models/outreach-form-data';

@Component({
  selector: 'app-crf-outreach-form',
  standalone: true,
  imports: [FormsModule, ReactiveFormsModule],
  templateUrl: './crf-outreach-form.component.html',
  styleUrl: './crf-outreach-form.component.scss',
})
export class CRFOutreachFormComponent implements OnInit, OnDestroy {
  @Input() outreachPrefillData!: OutreachFormData;
  @Input() connections: Connection[] = [];

  uniqueId: string | null = null;
  isIdInvalid = false;
  isSubmitted = false;
  loading = false;
  CDFIPreferences: Connection[] = [];
  selectedCDFIPreferences: Connection[] = [];
  dropDownOpen = false;
  maxCDFI = 2;
  showCDFIInfo = true;
  nameMaxLength = 100;
  businessNameMaxLength = 150;
  additionalDetailsMaxLength = 1000;

  // Default "any partner" option
  anyPartnerOption: Connection = {
    id: -1,
    display_name: 'Any partner organization can contact me',
    connection_name: 'Any partner organization',
  };

  emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  phonePattern = /^\+?[\d\s-()]{10,}$/;

  cdfiForm: CdfiForm = {
    name: '',
    email: '',
    phone: '',
    contactMethod: '',
    businessName: '',
    serviceRequest: '',
    connections: [],
    cdfi_contact_agree: false,
    additionalDetails: '',
    opt_out: false,
  };

  searchText = '';
  filteredCDFIPreferences: Connection[] = [];
  cdfiInfo = CDFI_INFO.filter((cdfi) => cdfi.state === 'NC');

  private completedFields = new Set<string>();
  private formStartTime = Date.now();

  route = inject(ActivatedRoute);
  outreachService = inject(OutreachService);
  clarityService = inject(MicrosoftClarityService);

  ngOnInit(): void {
    this.route.paramMap.subscribe((params) => {
      this.uniqueId = params.get('id');
      if (this.uniqueId === 'test-crf') {
        this.CDFIPreferences = [
          this.anyPartnerOption,
          ...CDFI_INFO.map((cdfi, idx) => ({
            id: idx,
            display_name: cdfi.name,
            connection_name: cdfi.name,
          })),
        ];
        this.filteredCDFIPreferences = [...this.CDFIPreferences];
      }
      if (this.uniqueId !== 'test-crf') {
        this.cdfiForm.businessName = this.outreachPrefillData?.name || '';
        this.cdfiForm.email = this.outreachPrefillData?.email || '';
        this.CDFIPreferences = [this.anyPartnerOption, ...this.connections];
        this.filteredCDFIPreferences = [...this.CDFIPreferences];
      }
    });

    // Add form abandonment tracking
    window.addEventListener('beforeunload', () => {
      if (!this.isSubmitted && this.completedFields.size > 0) {
        this.clarityService.sendClarityTag('form_abandoned', 'true');
        this.clarityService.sendClarityTag(
          'CRF_form_abandon_point',
          Array.from(this.completedFields).join(','),
        );
        this.clarityService.sendClarityTag(
          'CRF_form_completion_at_abandon',
          `${(this.completedFields.size / 6) * 100}%`,
        );
      }
    });
  }

  filterCDFIs() {
    if (!this.searchText) {
      this.filteredCDFIPreferences = [...this.CDFIPreferences];
      return;
    }

    const searchLower = this.searchText.toLowerCase();
    this.filteredCDFIPreferences = this.CDFIPreferences.filter((cdfi) =>
      (cdfi.display_name || cdfi.connection_name)
        .toLowerCase()
        .includes(searchLower),
    );
  }

  isEmailValid(): boolean {
    return this.cdfiForm.email
      ? this.emailPattern.test(this.cdfiForm.email)
      : true;
  }

  isPhoneValid(): boolean {
    return this.cdfiForm.phone.trim() || ''
      ? this.phonePattern.test(this.cdfiForm.phone)
      : true;
  }

  isNameValid(): boolean {
    return (this.cdfiForm.name?.length || 0) <= this.nameMaxLength;
  }

  isBusinessNameValid(): boolean {
    return (
      (this.cdfiForm.businessName?.length || 0) <= this.businessNameMaxLength
    );
  }

  isAdditionalDetailsValid(): boolean {
    return (
      (this.cdfiForm.additionalDetails?.length || 0) <=
      this.additionalDetailsMaxLength
    );
  }

  isFormValid(): boolean {
    return (
      this.cdfiForm.name?.trim() !== '' &&
      this.isNameValid() &&
      this.cdfiForm.email?.trim() !== '' &&
      this.isEmailValid() &&
      this.isPhoneValid() &&
      this.cdfiForm.contactMethod?.trim() !== '' &&
      this.cdfiForm.serviceRequest?.trim() !== '' &&
      (this.cdfiForm.cdfi_contact_agree || this.cdfiForm.opt_out) &&
      this.isBusinessNameValid() &&
      this.isAdditionalDetailsValid()
    );
  }

  trackFieldCompletion(fieldName: string) {
    if (!this.completedFields.has(fieldName)) {
      this.completedFields.add(fieldName);
      const completionRate = (this.completedFields.size / 6) * 100; // 6 required fields
      this.clarityService.sendClarityTag(
        'CRF_form_completion_rate',
        `${completionRate}%`,
      );
      this.clarityService.sendClarityTag('CRF_field_completed', fieldName);
    }
  }

  onFieldChange(fieldName: string, value: string) {
    if (value?.trim()) {
      this.trackFieldCompletion(fieldName);
    }
  }

  onSubmit() {
    if (this.uniqueId === 'test-crf') {
      this.isSubmitted = true;
    }

    if (this.isFormValid() && this.uniqueId) {
      // If "any partner" is selected, don't include specific connections
      if (
        this.selectedCDFIPreferences.some(
          (pref) => pref.id === this.anyPartnerOption.id,
        )
      ) {
        this.cdfiForm.connections = [];
      } else {
        this.cdfiForm.connections = this.selectedCDFIPreferences.map(
          (value, index) => ({
            connection_id: value.id,
            rank: index + 1,
          }),
        );
      }

      // Add completion time tracking
      this.clarityService.sendClarityTag(
        'CRF_form_completion_time',
        `${Date.now() - this.formStartTime}ms`,
      );

      this.outreachService.submitForm(this.uniqueId, this.cdfiForm).subscribe({
        next: () => {
          this.clarityService.sendClarityTag('CRF_form_submitted', 'true');
          this.isSubmitted = true;
        },
        error: (error) => {
          console.error('Form submission error:', error);
        },
      });
    }
  }

  toggleDropdown() {
    if (!this.cdfiForm.cdfi_contact_agree) {
      return;
    }
    this.dropDownOpen = !this.dropDownOpen;
    if (this.dropDownOpen) {
      this.searchText = '';
      this.filterCDFIs();
    }
  }

  removeTag(connection: Connection) {
    if (!this.cdfiForm.cdfi_contact_agree) {
      return;
    }
    // Clear any previous click event that might interfere
    event?.stopPropagation();
    this.selectedCDFIPreferences = this.selectedCDFIPreferences.filter(
      (value) => value.id !== connection.id,
    );
  }

  onCheckboxChange(value: Connection, event: Event) {
    if (!this.cdfiForm.cdfi_contact_agree) {
      return;
    }
    const selectedOption = this.CDFIPreferences.find(
      (option) => option.id === value.id,
    );
    if (!selectedOption) return;

    const isChecked = (event.target as HTMLInputElement).checked;

    // If selecting "any partner" option, clear other selections
    if (value.id === this.anyPartnerOption.id && isChecked) {
      this.selectedCDFIPreferences = [this.anyPartnerOption];
      this.clarityService.sendClarityTag('CRF_cdfi_selected', 'any_partner');
      return;
    }

    // If selecting specific partner while "any partner" is selected, clear "any partner"
    if (value.id !== this.anyPartnerOption.id && isChecked) {
      this.selectedCDFIPreferences = this.selectedCDFIPreferences.filter(
        (pref) => pref.id !== this.anyPartnerOption.id,
      );
    }

    if (isChecked) {
      if (this.selectedCDFIPreferences.length < this.maxCDFI) {
        this.selectedCDFIPreferences.push(value);
        this.clarityService.sendClarityTag(
          'CRF_cdfi_selected',
          value.display_name || value.connection_name,
        );
      }
    } else {
      this.selectedCDFIPreferences = this.selectedCDFIPreferences.filter(
        (item) => item !== value,
      );
    }
  }

  isOptionDisabled(option: Connection): boolean {
    if (!this.cdfiForm.cdfi_contact_agree) {
      return true;
    }

    // If "any partner" is selected, disable all other options
    if (
      this.selectedCDFIPreferences.some(
        (pref) => pref.id === this.anyPartnerOption.id,
      ) &&
      option.id !== this.anyPartnerOption.id
    ) {
      return true;
    }

    // If any specific partner is selected, disable "any partner" option
    if (
      this.selectedCDFIPreferences.length > 0 &&
      option.id === this.anyPartnerOption.id
    ) {
      return true;
    }

    return (
      this.selectedCDFIPreferences.length >= this.maxCDFI &&
      !this.selectedCDFIPreferences.includes(option)
    );
  }

  ngOnDestroy() {
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    window.removeEventListener('beforeunload', () => {});
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event) {
    if (!this.dropDownOpen) return;
    const target = event.target as HTMLElement;
    const isSearchInput = target.matches('.dropdown-search input');
    if (
      !target.closest('.form-group') ||
      (!isSearchInput && !target.closest('.dropdown-content'))
    ) {
      this.dropDownOpen = false;
    }
  }

  onFormClick(event: Event) {
    event.stopPropagation();
  }

  handleKeydown(event: KeyboardEvent) {
    if (event.key === 'Enter' || event.key === ' ') {
      this.toggleDropdown();
      event.preventDefault();
    }
  }

  toggleCDFIInfo() {
    this.showCDFIInfo = !this.showCDFIInfo;
  }

  handleCDFIInfoKeydown(event: KeyboardEvent) {
    if (event.key === 'Enter' || event.key === ' ') {
      this.toggleCDFIInfo();
      event.preventDefault();
    }
  }
}
